// src/components/onboarding/onboardingFormat.js
import moment from "moment";
import {
  ACK_PAGES,
  RAIL_DOCUMENTS,
  RAIL_DONE,
  RAIL_STEP,
} from "./onboardingCopy";

// Dates as the receipt and the documents list print them, in the browser's
// zone: "Sep 15, 2026" and "9:41 AM".
export const fullDate = (iso) => (iso ? moment(iso).format("MMM D, YYYY") : "");
export const clockTime = (iso) => (iso ? moment(iso).format("h:mm A") : "");

// The file name a downloaded copy is saved under — the same name the server
// puts in Content-Disposition (R135), built here because a cross-origin
// response header is not readable without an expose rule.
export const pdfName = (name, versionLabel) => `${name} ${versionLabel}.pdf`;

// The rail's lit label from GET /onboarding/stage `rail` (derived from the
// server's list, decision 12), or from `documentsRailFor` below for a page
// shown read-only (ruling R174): its own position, not the client's.
export const railLabelFor = (rail) => {
  if (!rail) return RAIL_DOCUMENTS;
  return rail.done ? RAIL_DONE : RAIL_STEP(rail.index, rail.count);
};

// A read-only documents page's OWN rail position (ruling R174) — the client's
// `stage.rail` is the CURRENT stage's, wrong for a page shown read-only.
// Derived from the served `stages` order (R129) rather than a position the
// client holds itself: n is 1-based among the served documents_* keys
// (documents_receipt excluded — it is "done", not a numbered step).
export const documentsRailFor = (stageKey, stages) => {
  const steps = (stages || []).filter(
    (s) => s.key.startsWith("documents_") && s.key !== "documents_receipt",
  );
  const index = steps.findIndex((s) => s.key === stageKey);
  return index === -1 ? null : { index: index + 1, count: steps.length };
};

// A disclosure row's meta from its version: "24 pages · Mar 2026", either half
// left out when the version does not carry it (never the comp's placeholders).
export const versionMeta = (version) =>
  [version?.pages ? ACK_PAGES(version.pages) : null, version?.editionLabel]
    .filter(Boolean)
    .join(" · ");

// A supplement's heading: "Priya Natarajan, Advisor".
export const advisorLine = (advisor) =>
  [
    `${advisor?.firstName ?? ""} ${advisor?.lastName ?? ""}`.trim(),
    advisor?.title,
  ]
    .filter(Boolean)
    .join(", ");

// The agreement's "from today, [date]" line: the America/Chicago civil date the
// server served the page on ('YYYY-MM-DD'), read as a calendar day so the
// browser's zone cannot move it: "Sep 15".
export const civilDay = (ymd) =>
  ymd ? moment(ymd, "YYYY-MM-DD").format("MMM D") : "";

// The name-mismatch comparison (spec § Agreement): NFKD-folded, combining
// marks stripped, whitespace collapsed, lower-cased. A mismatch is a hint,
// never a block.
export const foldName = (s) =>
  String(s ?? "")
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

// The server's signature-name rule: 2–100 non-whitespace graphemes after NFC
// and trim. Intl.Segmenter counts user-perceived characters; code points are
// the fallback where it is missing.
export function signableName(s) {
  const name = String(s ?? "")
    .normalize("NFC")
    .trim();
  const parts =
    typeof Intl !== "undefined" && typeof Intl.Segmenter === "function"
      ? [
          ...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(
            name,
          ),
        ].map((g) => g.segment)
      : [...name];
  const count = parts.filter((g) => !/^\s+$/.test(g)).length;
  return count >= 2 && count <= 100;
}
